import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DndProvider, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import ReactFlow, {
  Background,
  BackgroundVariant,
  Connection,
  Controls,
  Edge,
  EdgeChange,
  Node,
  NodeChange,
  ReactFlowInstance,
  ReactFlowProvider,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { v4 } from 'uuid'

import { Button, Flex, TabFooter } from 'styling/GlobalStyles'
import { CancelBtn } from 'components/Button/CancelBtn'
import { ClearButton } from 'components/Button/ClearButton'
import { ToggleButton } from 'components/Input/ToggleButton'
import { ItemsPick } from 'components/ItemsPick/ItemsPick'
import { IWorkflowTab } from 'types/workflows.interface'

import {
  WorkflowDropEmptyHint,
  WorkflowLogicContainer,
  WorkflowLogicToolbar,
} from './Workflows.styled'
import { TaskNode } from './Components/TaskNode'
import { DeletableEdge } from './Components/DeletableEdge'
import {
  TaskNodeData,
  graphToItems,
  itemsToGraph,
} from './Components/workflowGraph'

const nodeTypes = { task: TaskNode }
const edgeTypes = { deletable: DeletableEdge }

type DropPayload = { id: number; name: string }

const WorkflowLogicTabInner = ({ state, setState, handleSubmit }: IWorkflowTab) => {
  const [linkType, setLinkType] = useState<1 | 2>(1)
  const [nodes, setNodes] = useState<Node<TaskNodeData>[]>([])
  const [edges, setEdges] = useState<Edge[]>([])
  const initialized = useRef(false)
  const flowRef = useRef<ReactFlowInstance | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (initialized.current) return
    if (!state.items || state.items.length === 0) return
    if (nodes.length > 0) {
      initialized.current = true
      return
    }
    const { nodes: n, edges: e } = itemsToGraph(state.items)
    setNodes(n)
    setEdges(e)
    initialized.current = true
  }, [state.items, nodes.length])

  useEffect(() => {
    if (!initialized.current) return
    const rootIds = new Set(
      nodes.filter((n) => !edges.some((e) => e.target === n.id)).map((n) => n.id),
    )
    const needsUpdate = nodes.some((n) => Boolean(n.data.isRoot) !== rootIds.has(n.id))
    if (!needsUpdate) return
    setNodes((prev) =>
      prev.map((n) => ({ ...n, data: { ...n.data, isRoot: rootIds.has(n.id) } })),
    )
  }, [nodes, edges])

  useEffect(() => {
    const items = graphToItems(nodes, edges)
    setState((prev) =>
      JSON.stringify(prev.items) === JSON.stringify(items) ? prev : { ...prev, items },
    )
  }, [nodes, edges, setState])

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((ns) => applyNodeChanges(changes, ns)),
    [],
  )

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((es) => applyEdgeChanges(changes, es)),
    [],
  )

  const onConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target || connection.source === connection.target)
        return
      setEdges((es) => {
        const exists = es.some(
          (e) => e.source === connection.source && e.target === connection.target,
        )
        if (exists) return es
        return addEdge(
          {
            ...connection,
            type: 'deletable',
            animated: linkType === 2,
            data: { linkType },
          },
          es,
        )
      })
    },
    [linkType],
  )

  const addNodeFromTask = useCallback(
    (item: DropPayload, position: { x: number; y: number }) => {
      const id = `node_${v4()}`
      const newNode: Node<TaskNodeData> = {
        id,
        type: 'task',
        position,
        data: { taskTemplateId: item.id, name: item.name },
      }
      setNodes((ns) => [...ns, newNode])
    },
    [],
  )

  const [{ isOver }, dropRef] = useDrop<DropPayload, void, { isOver: boolean }>(
    () => ({
      accept: 'item',
      drop: (item, monitor) => {
        const offset = monitor.getClientOffset()
        const bounds = wrapperRef.current?.getBoundingClientRect()
        if (!offset || !bounds || !flowRef.current) return
        const position = flowRef.current.project({
          x: offset.x - bounds.left,
          y: offset.y - bounds.top,
        })
        addNodeFromTask(item, position)
      },
      collect: (monitor) => ({ isOver: !!monitor.isOver() }),
    }),
    [addNodeFromTask],
  )

  const setRefs = useCallback(
    (el: HTMLDivElement | null) => {
      wrapperRef.current = el
      dropRef(el)
    },
    [dropRef],
  )

  const handleClearLogic = () => {
    setNodes([])
    setEdges([])
  }

  const selectedTaskTemplates = useMemo(
    () => nodes.map((n) => ({ id: n.data.taskTemplateId, name: n.data.name })),
    [nodes],
  )

  return (
    <>
      <Flex gap='1rem' height='90%'>
        <WorkflowLogicContainer ref={setRefs}>
          <WorkflowLogicToolbar>
            <ToggleButton
              state={linkType === 2}
              handleChange={() => setLinkType(linkType === 2 ? 1 : 2)}
              label='Optional links'
            />
          </WorkflowLogicToolbar>
          <WorkflowDropEmptyHint visible={nodes.length === 0 && !isOver}>
            Drag tasks from the right panel to start building your workflow
          </WorkflowDropEmptyHint>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={(instance) => {
              flowRef.current = instance
              if (nodes.length > 0) instance.fitView({ padding: 0.2 })
            }}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            minZoom={0.2}
            maxZoom={1.5}
            defaultEdgeOptions={{ type: 'deletable' }}
            proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </WorkflowLogicContainer>
        <ItemsPick
          link='task/template/overviews'
          width='30%'
          selectedList={selectedTaskTemplates}
          oneColumn
          extraParams={{ isLogicAdded: true }}
          placeholder='Search Tasks'
          drag
        />
      </Flex>

      {handleSubmit && (
        <TabFooter>
          <Flex width='100%' end='true' gap='1rem'>
            <ClearButton text='Clear logic' onClick={handleClearLogic} />
            <CancelBtn />
            <Button height='40' onClick={handleSubmit} orange>
              Save
            </Button>
          </Flex>
        </TabFooter>
      )}
    </>
  )
}

export const WorkflowLocicTab = (props: IWorkflowTab) => {
  return (
    <DndProvider backend={HTML5Backend}>
      <ReactFlowProvider>
        <WorkflowLogicTabInner {...props} />
      </ReactFlowProvider>
    </DndProvider>
  )
}
